import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { fetchCommulativeChecklist } from "@/Redux/search/actions";
import { CustomDropdown } from "@/Components/common/Dropdown";
import { api_without_cancellation } from "@/api/api";
import FlagModal from "@/Components/Modals/flagModal";
import { setComponentLoadingEffect } from "@/Redux/common/Loader/action";
import { getCaseId, getClientId } from "@/Utils/helper";
import "@/Components/Navbars/detailBar.css";
import PinnedCasePopUp from "@/Components/Modals/PinnedCasePopUp";
import FlaggedRed from "@/assets/flagged-icons/redflag.svg";
import FlaggedGreen from "@/assets/flagged-icons/greenflag.svg";
import { fetchCriticalNotes } from "@/Redux/critical-notes/action";
import StatusModal from "@/Components/Navbars/status-modal/status-modal";
import CriticalNotePopup from "@/Components/Navbars/critical-note-modal/critical-note-modal";
import { CASES_MERGED_EVENT } from "@/Utils/caseMergeEvents";
import useCaseFlag from "@/features/flaggedCases/useCaseFlag";
import { shouldPlayIntro } from "@/Utils/introAnimations";
import { useCaseOpening } from "@/Providers/ProtectedRoute";
// import { fetchCommulativeChecklist } from "../../Redux/search/actions";
// import { CustomDropdown } from "../common/Dropdown";
// import { api_without_cancellation } from "../../api/api";
// import FlagModal from "../Modals/flagModal";
// import { setComponentLoadingEffect } from "../../Redux/common/Loader/action";
// import { getCaseId, getClientId } from "../../Utils/helper";
// import "./detailBar.css";
// import PinnedCasePopUp from "../Modals/PinnedCasePopUp";
// import FlaggedRed from "../../assets/flagged-icons/redflag.svg";
// import FlaggedGreen from "../../assets/flagged-icons/greenflag.svg";
// import { fetchCriticalNotes } from "../../Redux/critical-notes/action";

// import StatusModal from "./status-modal/status-modal";
// import CriticalNotePopup from "./critical-note-modal/critical-note-modal";

// width consumed by one button beyond its widest text line:
// 18px left pad + 12px right pad + 18px slant protrusion (9 each side) + 22px gap
const ITEM_EXTRA_WIDTH = 40;

// Status intro: a "Calculating Case Statuses" bar sweeps across, then the statuses pop in one by
// one, left to right. Keep these in step with the keyframes in detailBar.css.
const STATUS_CALCULATE_MS = 1100;
const STATUS_POP_START_MS = 120;
const STATUS_POP_STAGGER_MS = 100;
const STATUS_POP_MS = 450;

// split a status name into two balanced lines.
// single word -> one line. multi-word -> break at the word boundary
// that minimizes the longer line (by character count).
const splitLabel = (name = "") => {
  const words = name.trim().split(/\s+/);
  if (words.length <= 1) return [name];

  let best = 1;
  let bestMax = Infinity;
  for (let i = 1; i < words.length; i++) {
    const line1 = words.slice(0, i).join(" ").length;
    const line2 = words.slice(i).join(" ").length;
    const max = Math.max(line1, line2);
    if (max < bestMax) {
      bestMax = max;
      best = i;
    }
  }
  return [words.slice(0, best).join(" "), words.slice(best).join(" ")];
};

const DetailBar = (props) => {
  const dispatch = useDispatch();
  const headerData = useSelector((state) => state?.headerData.headerSummary);

  const currentCase = useSelector((state) => state?.caseData?.current);
  const stages = headerData?.stages_data;
  const client = headerData?.client_data;

  const hasCriticalNotes = useSelector((state) => state?.criticalNotes?.hasCriticalNotes);

  const location = useLocation();
  const link = location.pathname.split("/")[1] || "";
  const [flagModalShow, setFlagModalShow] = useState(false);
  const [isUnflagging, setIsUnflagging] = useState(false);
  const clientId = getClientId();
  const caseId = getCaseId();
  const {
    flagPage,
    positions: flagPositions,
    isLoading: isFlagLoading,
    loadFlagState,
    flagCase,
    unflagCase,
  } = useCaseFlag({
    clientId,
    caseId,
    pageId: props?.flaggedPageName,
    link,
    setNewFlags: props.setNewFlags,
  });

  useEffect(() => {
    setFlagModalShow(false);
  }, [clientId, caseId, props?.flaggedPageName]);
  const dynamicWidth = useSelector((state) => state.dynamicWidth.dynamicWidth);

  const [pinnedCaseModal, setPinnedCaseModal] = useState(false);
  const [pinnedCases, setPinnedCases] = useState({});

  const [showCriticalNote, setShowCriticalNote] = useState(false);

  const handleClose = () => {
    setPinnedCaseModal(false);
  };

  useEffect(() => {
    const storedClientId = getClientId();
    const storedCaseId = getCaseId();
    if (!client || !currentCase || !storedClientId || !storedCaseId) return;
    dispatch(fetchCommulativeChecklist(storedClientId, storedCaseId));
    dispatch(fetchCriticalNotes(storedClientId, storedCaseId));
  }, [dispatch, client, currentCase, props?.flaggedPageName]);

  useEffect(() => {
    let requestId = 0;
    let disposed = false;
    const refreshPinnedCases = async () => {
      const request = ++requestId;
      try {
        await api_without_cancellation.get("/api/pinned-cases/").then((response) => {
          if (!disposed && request === requestId) setPinnedCases(response.data);
        });
      } catch (error) {
        console.error("error", error);
      }
    };
    const onCasesMerged = () => {
      refreshPinnedCases();
    };
    refreshPinnedCases();
    window.addEventListener(CASES_MERGED_EVENT, onCasesMerged);
    return () => {
      disposed = true;
      window.removeEventListener(CASES_MERGED_EVENT, onCasesMerged);
    };
  }, []);
  useEffect(() => {
    if (stages) {
      dispatch(setComponentLoadingEffect("detailBar", false));
    }
  }, [stages, currentCase?.auto_case_stage, dispatch]);

  const open = useSelector((state) => state?.open?.open);

  const breakpoint = 900;
  const breakpoint2 = 1080;
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= breakpoint);
  const [isSmallScreen2, setIsSmallScreen2] = useState(window.innerWidth <= breakpoint2);
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= breakpoint);
      setIsSmallScreen2(window.innerWidth <= breakpoint2);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0);

  // critical pseudo-status is prepended so it participates in the same
  // measurement / fitting pass and always wins the first slot
  const displayStatuses = useMemo(() => {
    const base = props.statuses ?? [];
    return hasCriticalNotes ? [{ name: "Critical Note", isCritical: true }, ...base] : base;
  }, [props.statuses, hasCriticalNotes]);

  useLayoutEffect(() => {
    if (!measureRef.current || !containerRef.current || displayStatuses.length === 0) {
      return;
    }

    const calculateVisibleItems = () => {
      const container = containerRef.current;
      const containerWidth = container.offsetWidth;

      if (containerWidth === 0) {
        return;
      }

      const items = measureRef.current.querySelectorAll(".status-measure-item");

      // each item's offsetWidth = widest of its two wrapped lines
      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < items.length; i++) {
        const itemWidth = items[i].offsetWidth + ITEM_EXTRA_WIDTH;

        if (usedWidth + itemWidth <= containerWidth) {
          usedWidth += itemWidth;
          count++;
        } else {
          break;
        }
      }

      setVisibleCount(Math.min(count, displayStatuses.length));
    };

    calculateVisibleItems();

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleItems();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [displayStatuses]);

  const visibleStatuses = displayStatuses.slice(0, visibleCount);

  // "calculating" → "revealing" → "idle", once per case. A layout effect, so the statuses never
  // paint for a frame before the calculating bar covers them.
  const [statusIntro, setStatusIntro] = useState("idle");
  // The page mounts hidden behind the "Opening Case" popup: wait until it closes so it's seen, and
  // replay on every case opening (going back to an earlier case included).
  const { loadingScreenVisible, caseOpenings } = useCaseOpening();
  const statusIntroKey = `${currentCase?.id ?? caseId}:${caseOpenings}`;
  const hasVisibleStatuses = visibleCount > 0;
  useLayoutEffect(() => {
    if (!hasVisibleStatuses || isSmallScreen || loadingScreenVisible) return;
    if (!shouldPlayIntro("case-statuses", statusIntroKey)) return;
    const revealMs =
      STATUS_POP_START_MS + (visibleCount - 1) * STATUS_POP_STAGGER_MS + STATUS_POP_MS + 50;
    setStatusIntro("calculating");
    const reveal = setTimeout(() => setStatusIntro("revealing"), STATUS_CALCULATE_MS);
    const done = setTimeout(() => setStatusIntro("idle"), STATUS_CALCULATE_MS + revealMs);
    return () => {
      clearTimeout(reveal);
      clearTimeout(done);
      setStatusIntro("idle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVisibleStatuses, statusIntroKey, loadingScreenVisible]);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const handleStatusChange = (status) => {
    setShowStatusModal(true);
    setSelectedStatus(status);
  };

  const [preferences, setPreferences] = useState(null);
  const [isTabletView, setisTabletView] = useState(window.innerWidth < 1080);
  const shouldBeCollapsed = preferences?.SidebarView === true ? true : false;

  useEffect(() => {
    const getUserPreferencesFromStorage = () => {
      const preferences = localStorage.getItem("userPreferences");
      const preferencesObj = preferences ? JSON.parse(preferences) : null;
      setPreferences(preferencesObj);
    };
    getUserPreferencesFromStorage();
  }, []);

  useEffect(() => {
    const handleResize = () => setisTabletView(window.innerWidth < 1081);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {flagModalShow && (
        <FlagModal
          flagPage={isUnflagging ? unflagCase : flagCase}
          positions={flagPositions}
          isUnflagging={isUnflagging}
          show={flagModalShow}
          onHide={() => setFlagModalShow(false)}
        />
      )}
      <PinnedCasePopUp
        confirmPopUp={pinnedCaseModal}
        handleClose={handleClose}
        pinnedCases={pinnedCases}
        setPinnedCases={setPinnedCases}
      />
      {showCriticalNote && (
        <CriticalNotePopup show={showCriticalNote} handleClose={() => setShowCriticalNote(false)} />
      )}
      <div
        className="client-information bottom-row d-md-flex bg-white blurred-detail-bar"
        id="client-information"
        style={{ zIndex: "999" }}
      >
        <div
          className="client-details-wrap d-flex d-flex-lg-1 w-100"
          style={{
            paddingTop: "5px",
            paddingBottom: "5px",
            paddingLeft: shouldBeCollapsed || isTabletView ? "29px" : "122px",
            paddingRight: open ? "5px" : "8px",
            cursor: "pointer",
          }}
        >
          <button
            type="button"
            className="label-container-draft case-flag-button"
            aria-label={flagPage?.id ? "Unflag Case" : "Flag Case"}
            title={flagPage?.id ? "Unflag Case" : "Flag Case"}
            disabled={isFlagLoading || !clientId || !caseId || !props?.flaggedPageName}
            onClick={() => {
              setIsUnflagging(Boolean(flagPage?.id));
              setFlagModalShow(true);
              // Picks up position holders who changed since the page loaded.
              loadFlagState().catch(() => {});
            }}
          >
            <img
              src={flagPage?.id ? FlaggedRed : FlaggedGreen}
              className="ic ic-35"
              alt=""
              width={35}
              height={35}
            />
          </button>
          <div className="client-details-inner d-flex-lg-1">
            <div
              className="d-flex Header-width-100P align-items-start"
              style={{
                flexWrap: "wrap",
              }}
            >
              <div
                className="col client-details-first-col"
                style={{ flexBasis: "332.5px", paddingLeft: 133 }}
              >
                <div className="check-detail-items d-none">
                  <div className="d-flex">
                    <p className="font-weight-semibold">Car Accident</p>
                  </div>
                  <div className="d-flex">
                    <div className="text-label">DOI</div>
                    <p className="font-weight-semibold">10/25/2023</p>
                  </div>
                </div>
              </div>

              <div className="d-flex-lg-1 p-b-0" style={{ height: "45px" }}>
                {/* Hidden measurement container */}
                <div
                  ref={measureRef}
                  style={{
                    position: "absolute",
                    visibility: "hidden",
                    pointerEvents: "none",
                    display: "flex",
                    flexWrap: "nowrap",
                    alignItems: "flex-start",
                  }}
                >
                  {displayStatuses.map((status, i) => (
                    <div
                      key={`measure-${i}`}
                      className="status-measure-item"
                      style={{
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {splitLabel(status.name).map((line, j) => (
                        <span key={j} style={{ whiteSpace: "nowrap" }}>
                          {line}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Actual visible container */}
                <div
                  className="d-flex status-column"
                  style={{
                    marginLeft: `${
                      dynamicWidth -
                      308 +
                      6 +
                      (props.width ?? 0) +
                      (shouldBeCollapsed || isTabletView ? 93 : 0) +
                      (isSmallScreen2 ? 1 : 0)
                    }px`,
                    height: "45px",
                  }}
                >
                  <div
                    className={`status-intro-${statusIntro}`}
                    style={{
                      display: isSmallScreen ? "none" : "flex",
                      flexWrap: "nowrap",
                      position: "relative",
                      width: "100%",
                      gap: "14px",
                    }}
                    ref={containerRef}
                  >
                    {statusIntro !== "idle" && (
                      <div
                        className="status-calc"
                        role={statusIntro === "calculating" ? "status" : undefined}
                      >
                        <div className="status-calc-fill">
                          <span className="status-calc-text">
                            Calculating Case Statuses
                            <span className="status-calc-dots" aria-hidden="true" />
                          </span>
                        </div>
                      </div>
                    )}
                    {visibleStatuses.map((status, i) => {
                      const lines = splitLabel(status.name);
                      const isCritical = status.isCritical === true;
                      return (
                        <div
                          className={`status-stack-btn${isCritical ? " critical-status-btn" : ""}`}
                          style={{ "--status-i": i }}
                          key={`status-${i}`}
                          onClick={() =>
                            isCritical ? setShowCriticalNote(true) : handleStatusChange(status)
                          }
                        >
                          <div
                            className="status-stack-inner"
                            style={{
                              justifyContent: lines.length === 1 ? "center" : "space-between",
                              ...(isCritical && {
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "5px",
                                // minWidth: "80px",
                              }),
                            }}
                          >
                            {isCritical && (
                              <span className="d-flex align-items-center justify-content-center">
                                <img
                                  src="https://simplefirm-bucket.s3.amazonaws.com/static/images/critical.svg"
                                  className="ic ic-15 d-flex align-items-center justify-content-center"
                                />
                              </span>
                            )}
                            {isCritical && (
                              <div
                                className="d-flex flex-column"
                                style={{
                                  alignItems: "start",
                                }}
                              >
                                {lines.map((line, j) => (
                                  <div
                                    key={`line-${i}-${j}`}
                                    className={`status-stack-label${
                                      j === 0 && lines.length === 2 ? " status-stack-label-top" : ""
                                    }`}
                                  >
                                    {line}
                                  </div>
                                ))}
                              </div>
                            )}
                            {!isCritical &&
                              lines.map((line, j) => (
                                <div
                                  key={`line-${i}-${j}`}
                                  className={`status-stack-label${
                                    j === 0 && lines.length === 2 ? " status-stack-label-top" : ""
                                  }`}
                                >
                                  {line}
                                </div>
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CustomDropdown />
      </div>

      {showStatusModal && (
        <StatusModal
          show={showStatusModal}
          handleClose={() => setShowStatusModal(false)}
          selectedStatus={selectedStatus}
        />
      )}
    </>
  );
};

export default React.memo(DetailBar);
